import React, { useEffect, useMemo, useState } from "react";
import Shell, { StatCard, Section } from "@/pages/Shell";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import MyProject from "@/pages/investor/MyProject";
import ProgressGallery from "@/pages/investor/ProgressGallery";
import FAQ from "@/pages/investor/FAQ";
import ReferralProgram from "@/pages/investor/ReferralProgram";
import InvestorPmsPreview from "@/pages/investor/InvestorPmsPreview";
import NovedadesPanel from "@/components/investor/NovedadesPanel";
import useInvestorLanguage, { InvestorLanguageProvider } from "@/i18n/useInvestorLanguage";
import { FileUploader } from "@/components/ui/file-uploader";
import {
  FolderOpen, Buildings, Calendar,
  ChatCircle, FileText, House,
  Question, Receipt, Files, Scales, Briefcase, Share, Bed,
} from "@phosphor-icons/react";

// Tab key + icon are static; label is translated via useInvestorLanguage()
// inside the component so the strip re-renders when the investor switches
// preferred language. Six-item nav (approved 2028-08-26): Referrals + Help
// are now surfaced from within Mi cuenta; the ROI simulator (Performance)
// was retired since real bookings/performance live in the Bookings tab.
const BASE_TABS = [
  { key: "my_project",  icon: House },
  { key: "payments",    icon: Calendar },
  { key: "progress",    icon: Buildings },
  { key: "pms_preview", icon: Bed },
  { key: "documents",   icon: FolderOpen },
  { key: "my_account",  icon: ChatCircle },
];

const ACCENT = "#2F3E35";
const INVESTOR_IMG = "https://images.pexels.com/photos/36418268/pexels-photo-36418268.jpeg";

/**
 * Aggregate "has unseen progress activity" across all villas of the investor.
 * A dot appears in the "Obra en vivo" tab if any villa has a last_update_at
 * newer than the per-villa `gallery_last_seen_{villa_id}` timestamp in
 * localStorage. The dot disappears as soon as the investor opens the tab.
 */
function useGalleryUnseen() {
  const [unseen, setUnseen] = useState(false);

  const recompute = async () => {
    try {
      const { data: villas } = await api.get("/villas");
      if (!villas || villas.length === 0) { setUnseen(false); return; }
      const details = await Promise.all(villas.map(v => api.get(`/villas/${v.villa_id}`).catch(() => null)));
      const any = details.some(r => {
        if (!r) return false;
        const lastUpdate = r.data?.summary?.recent_activity?.last_update_at;
        if (!lastUpdate) return false;
        const sevenDaysAgo = Date.now() - 7 * 86400000;
        if (new Date(lastUpdate).getTime() < sevenDaysAgo) return false;
        const lastSeen = localStorage.getItem(`gallery_last_seen_${r.data.villa.villa_id}`);
        return !lastSeen || lastSeen < lastUpdate;
      });
      setUnseen(any);
    } catch { setUnseen(false); }
  };

  useEffect(() => { recompute(); }, []);
  return { unseen, markSeen: (villas) => {
    const now = new Date().toISOString();
    villas.forEach(v => localStorage.setItem(`gallery_last_seen_${v.villa_id}`, now));
    setUnseen(false);
  }, recompute };
}

// Public wrapper — provides the i18n context to the entire investor subtree.
export default function InvestorDashboard() {
  return (
    <InvestorLanguageProvider>
      <InvestorDashboardInner />
    </InvestorLanguageProvider>
  );
}

function InvestorDashboardInner() {
  const [tab, setTab] = useState("my_project");
  const { unseen, markSeen } = useGalleryUnseen();
  const { t, lang } = useInvestorLanguage();
  const { user } = useAuth();

  // Apply the investor's saved theme on every mount of the portal, not just
  // when Mi cuenta is opened. Without this, reloading directly on any tab
  // (my_project / progress / payments / …) left the .dark class unset until
  // the investor visited Mi cuenta.
  useEffect(() => {
    document.documentElement.classList.toggle("dark", user?.theme === "dark");
  }, [user?.theme]);

  // Listen for mobile bottom-nav tab change requests.
  useEffect(() => {
    const handler = (e) => {
      const next = e?.detail;
      if (typeof next === "string" && next) setTab(next);
    };
    window.addEventListener("dv:investor-tab", handler);
    return () => window.removeEventListener("dv:investor-tab", handler);
  }, []);

  // Light-weight aggregator for the Novedades panel — fetches the three
  // existing endpoints (no backend changes) once and passes them down.
  // The Documents/Payments tabs still fetch independently when entered;
  // this is a small duplicate cost for a cross-tab feature.
  const [me, setMe] = useState(null);
  const [novedadesData, setNovedadesData] = useState({ documents: [], payments: [], progress: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meRes, docsRes, payRes, villasRes] = await Promise.all([
          api.get("/auth/me").catch(() => null),
          api.get("/documents").catch(() => ({ data: [] })),
          api.get("/payments").catch(() => ({ data: [] })),
          api.get("/villas").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        if (meRes) setMe(meRes.data);
        // Aggregate progress across the investor's villas (typically 1).
        const villas = Array.isArray(villasRes.data) ? villasRes.data : [];
        let progress = [];
        if (villas.length > 0) {
          const details = await Promise.all(
            villas.map(v => api.get(`/villas/${v.villa_id}`).catch(() => null))
          );
          for (const d of details) {
            if (d?.data?.progress) progress = progress.concat(d.data.progress);
          }
        }
        if (cancelled) return;
        setNovedadesData({
          documents: Array.isArray(docsRes.data) ? docsRes.data : [],
          payments:  Array.isArray(payRes.data)  ? payRes.data  : [],
          progress,
        });
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Mark as seen when entering the progress tab
  useEffect(() => {
    if (tab !== "progress") return;
    (async () => {
      try {
        const { data: villas } = await api.get("/villas");
        markSeen(villas || []);
      } catch { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Tab labels come from the i18n dictionary so they update live when the
  // investor changes their preferred language from "Mi cuenta". "pms_preview"
  // has no i18n key yet — we use the short label "Bookings" / "Reservas" in
  // the strip itself (the internal page still explains it as Reservas &
  // Rendimiento — preview).
  const tabs = BASE_TABS.map(b => ({
    ...b,
    label: b.key === "pms_preview"
      ? (lang === "en" ? "Bookings" : "Reservas")
      : t(`tab.${b.key}`),
    ...(b.key === "progress" ? { dot: unseen } : {}),
  }));

  const userId = me?.user_id || me?.email || "anon";
  const locale = lang === "en" ? "en-GB" : "es-ES";

  return (
    <Shell tabs={tabs} activeTab={tab} onTabChange={setTab}>
      <NovedadesPanel
        userId={userId}
        documents={novedadesData.documents}
        payments={novedadesData.payments}
        progress={novedadesData.progress}
        t={t}
        locale={locale}
        onOpenEntity={(e) => {
          if (!e) return;
          if (e.kind === "document") setTab("documents");
          else if (e.kind === "payment") setTab("payments");
          else if (e.kind === "progress") setTab("progress");
        }}
      />
      {tab === "my_project" && <MyProject onNavigate={setTab} />}
      {tab === "my_account" && <MyAccount />}
      {tab === "progress" && <ProgressGallery />}
      {tab === "documents" && <Documents />}
      {tab === "payments" && <Payments />}
      {/* referral + help stay reachable from within Mi cuenta via the
          dv:investor-tab event bridge; they're not in the top strip. */}
      {tab === "help" && <FAQ />}
      {tab === "referral" && <ReferralProgram />}
      {tab === "pms_preview" && <InvestorPmsPreview />}
    </Shell>
  );
}

// Note: the ROI simulator (computeROI + ROICalculator + Slider/TaxRow/FixedRow
// helpers) was retired together with the Performance tab (2028-08-26). Real
// bookings/performance data now live in the Bookings tab (pms_preview) and
// the retired simulator is preserved in git history for future reference.

/**
 * Canonicalize a document into one of the investor-facing categories. We
 * keep the backend `category` / `doc_type` data shape and *interpret* it on
 * the frontend, so existing data keeps working.
 */
const DOC_CATEGORIES = [
  { key: "contracts",      label: "Contratos",            icon: FileText, match: (d) => /contract|contrato/i.test(`${d.category} ${d.doc_type} ${d.title}`) },
  { key: "presentations",  label: "Presentaciones",       icon: Briefcase, match: (d) => /present/i.test(`${d.category} ${d.doc_type} ${d.title}`) },
  { key: "dossier",        label: "Dossier de inversión", icon: Files,    match: (d) => /dossier|brochure/i.test(`${d.category} ${d.doc_type} ${d.title}`) },
  { key: "construction",   label: "Reportes de obra",     icon: Buildings, match: (d) => /construction|obra|progress|report/i.test(`${d.category} ${d.doc_type} ${d.title}`) },
  { key: "payment_receipts", label: "Recibos de pago",    icon: Receipt,  match: (d) => /(receipt|recibo|invoice|factura|payment)/i.test(`${d.category} ${d.doc_type} ${d.title}`) },
  { key: "legal",          label: "Documentos legales",   icon: Scales,   match: (d) => /(legal|licens|permit|notar)/i.test(`${d.category} ${d.doc_type} ${d.title}`) },
  { key: "other",          label: "Otros documentos",     icon: FolderOpen, match: () => true }, // fallback
];

function canonicalCategory(doc) {
  for (const c of DOC_CATEGORIES) {
    if (c.match(doc)) return c.key;
  }
  return "other";
}

function Documents() {
  const { t, lang } = useInvestorLanguage();
  const locale = lang === "en" ? "en-GB" : "es-ES";
  const [docs, setDocs] = useState(null);  // null = loading
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => {
    api.get("/documents")
      .then(r => setDocs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDocs([]));
  }, []);

  const visibleDocs = useMemo(() => {
    return (docs || []).filter(d => (d.category || "").toLowerCase() !== "faq");
  }, [docs]);

  const groupedByCat = useMemo(() => {
    const acc = {};
    for (const d of visibleDocs) {
      const key = canonicalCategory(d);
      (acc[key] ||= []).push(d);
    }
    return acc;
  }, [visibleDocs]);

  const presentCategories = DOC_CATEGORIES.filter(c => (groupedByCat[c.key] || []).length > 0);
  const catLabel = (c) => t(`documents.cat.${c.key}`);

  if (docs === null) {
    return (
      <Section title={t("documents.title")} subtitle={t("documents.subtitle")}>
        <div className="dv-card text-center py-12 text-stone-500 dark:text-stone-400">{t("documents.loading")}</div>
      </Section>
    );
  }

  return (
    <Section title={t("documents.title")} subtitle={t("documents.subtitle")}>
      <div className="flex gap-2 flex-wrap mb-4" data-testid="documents-filters">
        <button
          className={`dv-btn ${activeCat === "all" ? "dv-btn-primary" : "dv-btn-ghost"}`}
          onClick={() => setActiveCat("all")}
          data-testid="doc-filter-all"
        >
          {t("documents.filter.all")} ({visibleDocs.length})
        </button>
        {presentCategories.map(c => (
          <button
            key={c.key}
            className={`dv-btn ${activeCat === c.key ? "dv-btn-primary" : "dv-btn-ghost"}`}
            onClick={() => setActiveCat(c.key)}
            data-testid={`doc-filter-${c.key}`}
          >
            {catLabel(c)} ({groupedByCat[c.key].length})
          </button>
        ))}
      </div>

      {visibleDocs.length === 0 ? (
        <div className="dv-card text-center py-12 text-stone-500 dark:text-stone-400">
          {t("documents.empty")}
        </div>
      ) : (
        <div className="space-y-6">
          {presentCategories
            .filter(c => activeCat === "all" || activeCat === c.key)
            .map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.key} data-testid={`doc-group-${c.key}`}>
                  <h3 className="font-serif-dv text-xl mb-3 flex items-center gap-2">
                    <Icon size={20} weight="light" className="text-stone-500 dark:text-stone-400" />
                    {catLabel(c)}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {groupedByCat[c.key].map((d) => (
                      <div key={d.doc_id} className="dv-card flex items-center gap-3 sm:gap-4" data-testid={`doc-${d.doc_id}`}>
                        <div className="w-12 h-12 rounded-md bg-stone-100 dark:bg-stone-800 flex items-center justify-center shrink-0">
                          <Icon size={22} weight="light" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{d.title}</div>
                          {/* Mobile fix: sub-line gets truncate so a long doc_type
                              never pushes the "Abrir" button off-screen. */}
                          <div className="text-xs text-stone-500 dark:text-stone-400 truncate">
                            {d.doc_type ? `${d.doc_type} · ` : ""}
                            {(d.document_date || d.created_at) ? new Date(d.document_date || d.created_at).toLocaleDateString(locale) : ""}
                          </div>
                        </div>
                        {(d.file_id || d.file_url) && (
                          <a
                            href={d.file_id
                              ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${d.file_id}`
                              : d.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="dv-btn dv-btn-primary !py-2 shrink-0"
                          >
                            {t("documents.open")}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </Section>
  );
}

function MyAccount() {
  const { t, lang, setLang } = useInvestorLanguage();
  const { setUser } = useAuth();
  const [me, setMe] = useState(null);
  const [name, setName] = useState("");
  const [theme, setTheme] = useState("light");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      setMe(data); setName(data.name || ""); setTheme(data.theme || "light");
      document.documentElement.classList.toggle("dark", (data.theme || "light") === "dark");
    });
  }, []);
  // Language is persisted by useInvestorLanguage()'s setLang() (which patches
  // /auth/me). Saving the profile here only updates name + theme to keep the
  // two flows orthogonal — the language select takes effect immediately.
  const save = async () => {
    const { data } = await api.patch("/auth/me", { name, theme });
    setMe(data);
    // Sync the auth context so the root-mount theme effect stays in sync
    // and doesn't hold a stale theme after a save.
    setUser(data);
    document.documentElement.classList.toggle("dark", theme === "dark");
    alert(t("account.saved"));
  };
  const changePassword = async () => {
    await api.post("/auth/change-password", pw);
    alert(t("account.password.done"));
  };
  if (!me) {
    return (
      <Section title={t("account.title")}>
        <div className="dv-card">{t("documents.loading")}</div>
      </Section>
    );
  }
  return (
    <Section title={t("account.title")} subtitle={t("account.subtitle")}>
      <div className="dv-card space-y-4 max-w-2xl">
        <div>
          <label className="text-sm">{t("account.email")}</label>
          <input className="dv-input mt-1" value={me.email || ""} readOnly />
        </div>
        <div>
          <label className="text-sm">{t("account.name")}</label>
          <input className="dv-input mt-1" value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm">{t("account.language")}</label>
            <select
              className="dv-input mt-1"
              value={lang}
              onChange={e => setLang(e.target.value)}
              data-testid="account-language-select"
            >
              <option value="es">Español</option>
              <option value="en">English</option>
            </select>
          </div>
          <div>
            <label className="text-sm">{t("account.theme")}</label>
            <select className="dv-input mt-1" value={theme} onChange={e => setTheme(e.target.value)}>
              <option value="light">{t("account.theme.light")}</option>
              <option value="dark">{t("account.theme.dark")}</option>
            </select>
          </div>
        </div>
        <button className="dv-btn dv-btn-primary" onClick={save}>{t("account.save")}</button>
      </div>
      <div className="dv-card space-y-3 mt-4 max-w-2xl">
        <h3 className="font-medium">{t("account.password.title")}</h3>
        <input
          type="password" className="dv-input"
          placeholder={t("account.password.current")}
          value={pw.current_password}
          onChange={e => setPw({ ...pw, current_password: e.target.value })}
        />
        <input
          type="password" className="dv-input"
          placeholder={t("account.password.new")}
          value={pw.new_password}
          onChange={e => setPw({ ...pw, new_password: e.target.value })}
        />
        <button className="dv-btn dv-btn-primary" onClick={changePassword}>
          {t("account.password.submit")}
        </button>
      </div>

      {/* Sub-actions: surfaces retired from the top strip (Referrals + FAQ)
          reachable from here via the dv:investor-tab bridge. Logout is not
          duplicated — the app header already owns that action. Hidden below
          lg because the mobile avatar dropdown already surfaces both entries
          — no reason to double them up on phones. */}
      <div className="hidden lg:block dv-card space-y-3 mt-4 max-w-2xl" data-testid="account-quick-actions">
        <h3 className="font-medium">{lang === "en" ? "More" : "Más"}</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          <AccountAction
            icon={Share}
            label={t("tab.referral")}
            onClick={() => window.dispatchEvent(new CustomEvent("dv:investor-tab", { detail: "referral" }))}
            testid="account-open-referral"
          />
          <AccountAction
            icon={Question}
            label={t("tab.help")}
            onClick={() => window.dispatchEvent(new CustomEvent("dv:investor-tab", { detail: "help" }))}
            testid="account-open-help"
          />
        </div>
      </div>
    </Section>
  );
}

function AccountAction({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md border border-stone-200 dark:border-stone-700 text-sm text-stone-800 dark:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
    >
      {Icon && <Icon size={16} weight="regular" className="text-stone-500 dark:text-stone-400" />}
      <span className="flex-1 text-left">{label}</span>
    </button>
  );
}

/**
 * Map a free-form payment phase string into one of the canonical milestone
 * groups. Falls back to "other" so legacy phases never go missing.
 */
const PAYMENT_MILESTONES = [
  { key: "reserva",   tkey: "payments.phase.reserva",    target_pct: 10, match: /reserva|deposit|reservation/i },
  { key: "foundation",tkey: "payments.phase.foundation", target_pct: 40, match: /foundation|cimentaci/i },
  { key: "structure", tkey: "payments.phase.structure",  target_pct: 30, match: /structure|estructura|techo|roof|mep/i },
  { key: "handover",  tkey: "payments.phase.handover",   target_pct: 20, match: /handover|entrega|delivery/i },
];

function classifyPaymentPhase(phase) {
  const txt = String(phase || "");
  for (const m of PAYMENT_MILESTONES) {
    if (m.match.test(txt)) return m.key;
  }
  return "other";
}

// Map known backend `payment.phase` strings to translation keys.
// Unknown phases fall back to the raw backend label.
const PAYMENT_ROW_LABEL_MAP = {
  "Pago 1 · Reserva (10%)":            "payments.row.pago_1",
  "Pago 2 · Segundo pago (40%)":       "payments.row.pago_2",
  "Pago 3 · Tercer pago (30%)":        "payments.row.pago_3",
  "Pago 4 · Entrega de llaves (20%)":  "payments.row.pago_4",
};
function translatePhase(t, phase) {
  if (!phase) return "";
  const tkey = PAYMENT_ROW_LABEL_MAP[phase];
  if (tkey) {
    const tr = t(tkey);
    if (tr && tr !== tkey) return tr;
  }
  return phase;
}

// Visible name of a payment row. Prefers the admin-authored `title`. Falls
// back to translating a legacy auto-payment phase. Never surfaces the raw
// "other" sentinel — that becomes the generic i18n fallback ("Pago" / "Payment").
function paymentLabel(t, p) {
  const title = (p?.title || "").trim();
  if (title) return title;
  const phase = (p?.phase || "").trim();
  if (phase && phase !== "other") {
    const tr = translatePhase(t, phase);
    if (tr) return tr;
  }
  return t("payments.row.fallback");
}

function paymentUrgency(t, p) {
  if (p.status === "paid") return null;
  if (!p.due_date) return null;
  const due = new Date(p.due_date + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((due - today) / 86400000);
  const n = Math.abs(days);
  if (p.status === "overdue" || days < 0) {
    return {
      tone: "red",
      label: days < 0
        ? (n === 1 ? t("payments.urgency.overdue_one", { n }) : t("payments.urgency.overdue_many", { n }))
        : t("payments.status.overdue"),
      days,
    };
  }
  if (days <= 7) {
    return {
      tone: "red",
      label: days === 0
        ? t("payments.urgency.due_today")
        : (days === 1 ? t("payments.urgency.next_in_one", { n: days }) : t("payments.urgency.next_in_many", { n: days })),
      days,
    };
  }
  if (days <= 30) {
    return { tone: "amber", label: t("payments.urgency.next_in_many", { n: days }), days };
  }
  return { tone: "stone", label: t("payments.urgency.next_in_many", { n: days }), days };
}

function Payments() {
  const { t, lang } = useInvestorLanguage();
  const [payments, setPayments] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [notifyFor, setNotifyFor] = useState(null);
  // Server-resolved total investment (sum of agreed_price_eur across the
  // investor's villas). Null until the parallel fetches resolve; falls back to
  // the client-side sum-of-payments when missing or empty.
  const [totalInvestment, setTotalInvestment] = useState(null);
  const load = async () => {
    const [p, n] = await Promise.all([
      api.get("/payments"),
      api.get("/payment-notifications/mine").catch(() => ({ data: [] })),
    ]);
    // Defensive: the backend already filters archived for investors, but we
    // keep a client guard so a regression there never leaks archived rows.
    setPayments((p.data || []).filter((row) => row?.status !== "archived"));
    setNotifs(n.data);
    // Pull `summary.total_investment` per villa and sum. Best-effort: any
    // failure leaves totalInvestment null and the UI falls back to
    // sum(payments.amount) below.
    try {
      const villas = (await api.get("/villas")).data || [];
      if (Array.isArray(villas) && villas.length > 0) {
        const details = await Promise.all(
          villas.map((v) => api.get(`/villas/${v.villa_id}`).catch(() => null))
        );
        const total = details.reduce((acc, r) => {
          const ti = r?.data?.summary?.total_investment;
          return acc + (typeof ti === "number" ? ti : 0);
        }, 0);
        setTotalInvestment(total > 0 ? total : null);
      } else {
        setTotalInvestment(null);
      }
    } catch {
      setTotalInvestment(null);
    }
  };
  useEffect(() => { load(); }, []);

  const activeNotifFor = (pid) => notifs.find(n => n.payment_id === pid && n.status === "pending");

  // === Summary aggregation (client-side, derived from /payments) ===
  const summary = useMemo(() => {
    const paymentsTotal = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    // Prefer server-resolved agreed price; fall back to client sum when the
    // backend summary isn't reachable (offline, schema mismatch, etc.).
    const total = (typeof totalInvestment === "number" && totalInvestment > 0)
      ? totalInvestment
      : paymentsTotal;
    const paid  = payments.filter(p => p.status === "paid")
                         .reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const pending = Math.max(0, total - paid);

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const upcoming = payments
      .filter(p => p.status !== "paid" && p.due_date)
      .map(p => ({ ...p, _due: new Date(p.due_date + "T00:00:00") }))
      .sort((a, b) => a._due - b._due);
    const next = upcoming[0] || null;
    const nextUrgency = next ? paymentUrgency(t, next) : null;

    // Milestone summary
    const byMilestone = PAYMENT_MILESTONES.map(m => {
      const items = payments.filter(p => classifyPaymentPhase(p.phase) === m.key);
      const sum = items.reduce((s, p) => s + (Number(p.amount) || 0), 0);
      const paidSum = items.filter(p => p.status === "paid")
                          .reduce((s, p) => s + (Number(p.amount) || 0), 0);
      return { ...m, label: t(m.tkey), items, total: sum, paid: paidSum, pct_of_total: paymentsTotal ? (sum / paymentsTotal) * 100 : 0 };
    });
    const otherItems = payments.filter(p => classifyPaymentPhase(p.phase) === "other");
    if (otherItems.length > 0) {
      const sum = otherItems.reduce((s, p) => s + (Number(p.amount) || 0), 0);
      const paidSum = otherItems.filter(p => p.status === "paid")
                                .reduce((s, p) => s + (Number(p.amount) || 0), 0);
      byMilestone.push({
        key: "other", label: t("payments.phase.other"), target_pct: null, items: otherItems,
        total: sum, paid: paidSum, pct_of_total: paymentsTotal ? (sum / paymentsTotal) * 100 : 0,
      });
    }
    return { total, paid, pending, next, nextUrgency, byMilestone };
  }, [payments, t, totalInvestment]);

  const locale = lang === "en" ? "en-GB" : "es-ES";
  const fmtEur = (n) => `€${Math.round(Number(n) || 0).toLocaleString(locale)}`;
  const fmtDate = (iso) => iso ? new Date(iso + "T00:00:00").toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" }) : "—";
  const toneClass = (tone) => ({
    red:   "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800/60",
    amber: "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/60",
    stone: "bg-stone-50 dark:bg-stone-800 text-stone-700 dark:text-stone-200 border-stone-200 dark:border-stone-700",
  }[tone] || "bg-stone-50 dark:bg-stone-800 text-stone-700 dark:text-stone-200 border-stone-200 dark:border-stone-700");

  return (
    <Section title={t("payments.title")} subtitle={t("payments.subtitle")}>
      {payments.length === 0 ? (
        <div className="dv-card text-center py-12 text-stone-500 dark:text-stone-400">{t("payments.empty")}</div>
      ) : (<>
        {/* === Resumen de pagos === */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="payments-summary">
          <StatCard label={t("payments.summary.total")}    value={fmtEur(summary.total)}   accent={ACCENT} testid="payments-summary-total" />
          <StatCard label={t("payments.summary.paid")}     value={fmtEur(summary.paid)}    accent={ACCENT} testid="payments-summary-paid" />
          <StatCard label={t("payments.summary.pending")}  value={fmtEur(summary.pending)} accent={ACCENT} testid="payments-summary-pending" />
          <div className="dv-card" data-testid="payments-summary-next" style={{ "--dv-accent": ACCENT }}>
            <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-1">{t("payments.summary.next")}</div>
            <div className="font-serif-dv text-xl dv-stat-value">
              {summary.next ? fmtEur(summary.next.amount) : "—"}
            </div>
            <div className="text-xs text-stone-500 dark:text-stone-400 mt-1">
              {summary.next ? fmtDate(summary.next.due_date) : t("payments.next.none")}
            </div>
            {summary.nextUrgency && (
              <div className={`inline-block mt-2 text-[11px] px-2 py-0.5 rounded-full border ${toneClass(summary.nextUrgency.tone)}`}>
                {summary.nextUrgency.label}
              </div>
            )}
          </div>
        </div>

        {/* === Calendario por hitos === */}
        <div className="dv-card mb-6" data-testid="payments-milestones">
          <div className="flex items-baseline justify-between gap-3 mb-3">
            <div className="text-xs tracking-widest uppercase text-stone-500 dark:text-stone-400">{t("payments.milestones.title")}</div>
            {/* Mobile swipe hint — visible only on small screens where the
                table scrolls horizontally. */}
            <div className="sm:hidden text-[11px] text-stone-400 dark:text-stone-500">
              {t("payments.milestones.swipe_hint")}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-stone-500 dark:text-stone-400">
                <tr className="border-b border-stone-200 dark:border-stone-700">
                  <th className="px-3 py-2 whitespace-nowrap">{t("payments.milestones.col.hito")}</th>
                  <th className="px-3 py-2 whitespace-nowrap">{t("payments.milestones.col.target")}</th>
                  <th className="px-3 py-2 whitespace-nowrap">{t("payments.milestones.col.actual")}</th>
                  <th className="px-3 py-2 whitespace-nowrap">{t("payments.milestones.col.amount")}</th>
                  <th className="px-3 py-2 whitespace-nowrap">{t("payments.milestones.col.paid")}</th>
                </tr>
              </thead>
              <tbody>
                {summary.byMilestone.map(m => (
                  <tr key={m.key} className="border-b border-stone-100 dark:border-stone-800" data-testid={`milestone-row-${m.key}`}>
                    <td className="px-3 py-2 font-medium whitespace-nowrap">{m.label}</td>
                    <td className="px-3 py-2 text-stone-600 dark:text-stone-300 whitespace-nowrap">
                      {m.target_pct != null ? `${m.target_pct}%` : "—"}
                    </td>
                    <td className="px-3 py-2 text-stone-600 dark:text-stone-300 whitespace-nowrap">{Math.round(m.pct_of_total)}%</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtEur(m.total)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={m.paid >= m.total && m.total > 0 ? "text-emerald-700 dark:text-emerald-400" : "text-stone-700 dark:text-stone-200"}>
                        {fmtEur(m.paid)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

          {/* Mobile: card list */}
          <div className="grid gap-3 md:hidden" data-testid="payments-mobile-list">
            {payments.map((p) => (
              <PaymentCardMobile
                key={p.payment_id}
                payment={p}
                pendingNotif={activeNotifFor(p.payment_id)}
                onNotify={() => setNotifyFor(p)}
                t={t}
                dateLocale={locale}
              />
            ))}
          </div>

          {/* Desktop: table */}
          <div className="dv-card p-0 overflow-hidden hidden md:block">
            <table className="w-full">
              <thead className="bg-stone-50 dark:bg-stone-800 border-b border-stone-200 dark:border-stone-700">
                <tr className="text-left text-xs tracking-wider uppercase text-stone-500 dark:text-stone-400">
                  <th className="px-5 py-3">{t("payments.col.phase")}</th>
                  <th className="px-5 py-3">{t("payments.col.due_date")}</th>
                  <th className="px-5 py-3">{t("payments.col.amount")}</th>
                  <th className="px-5 py-3">{t("payments.col.status")}</th>
                  <th className="px-5 py-3 text-right">{t("payments.col.action")}</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => {
                  const pending = activeNotifFor(p.payment_id);
                  return (
                    <tr key={p.payment_id} className="border-b border-stone-100 dark:border-stone-800" data-testid={`payment-${p.payment_id}`}>
                      <td className="px-5 py-4 font-medium">{paymentLabel(t, p)}</td>
                      <td className="px-5 py-4 text-stone-600 dark:text-stone-300">{p.due_date}</td>
                      <td className="px-5 py-4 font-serif-dv text-lg">{Number(p.amount).toLocaleString(locale)} {p.currency}</td>
                      <td className="px-5 py-4">
                        <span className={`dv-badge ${p.status === "paid" ? "dv-badge-baja" : p.status === "overdue" ? "dv-badge-alta" : "dv-badge-media"}`}>
                          {p.status === "paid" ? t("payments.status.paid") : p.status === "overdue" ? t("payments.status.overdue") : t("payments.status.pending")}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        {p.status === "paid" ? (
                          <span className="text-xs text-stone-400 dark:text-stone-500">—</span>
                        ) : pending ? (
                          <span className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 rounded px-2 py-1" data-testid={`notif-pending-${p.payment_id}`}>
                            {t("payments.notify.pending")}
                          </span>
                        ) : (
                          <button
                            onClick={() => setNotifyFor(p)}
                            className="text-xs text-stone-700 dark:text-stone-200 hover:text-stone-900 dark:hover:text-stone-50 border border-stone-300 dark:border-stone-700 hover:border-stone-900 dark:hover:border-stone-50 rounded px-3 py-1 transition-colors"
                            data-testid={`notify-btn-${p.payment_id}`}
                          >
                            {t("payments.notify.btn")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>)}
      {notifyFor && <NotifyPaymentModal payment={notifyFor} onClose={() => setNotifyFor(null)} onSent={async () => { setNotifyFor(null); await load(); }} t={t} dateLocale={locale} />}
    </Section>
  );
}


/**
 * PaymentCardMobile — premium card view of a payment row on small screens.
 *
 * Visual states:
 *  - paid     → verde sobrio (badge "Pagado")
 *  - overdue  → rojo discreto (badge "Vencido hace N días")
 *  - due ≤7d  → ámbar suave   (badge "Vence hoy" / "Vence mañana" / "Vence en N días")
 *  - pending  → stone neutro  (badge "Pendiente")
 */
function PaymentCardMobile({ payment, pendingNotif, onNotify, t, dateLocale = "es-ES" }) {
  const p = payment;
  const due = p.due_date ? new Date(p.due_date + "T00:00:00") : null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const daysUntil = due ? Math.round((due - today) / 86400000) : null;

  const isPaid = p.status === "paid";
  const isOverdue = p.status === "overdue" || (p.status !== "paid" && daysUntil != null && daysUntil < 0);
  const isDueSoon = !isPaid && !isOverdue && daysUntil != null && daysUntil >= 0 && daysUntil <= 7;

  let badgeLabel = t("payments.status.pending");
  let badgeClass = "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-200 border-stone-200 dark:border-stone-700";
  if (isPaid) {
    badgeLabel = t("payments.status.paid");
    badgeClass = "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/60";
  } else if (isOverdue) {
    const n = Math.abs(daysUntil ?? 0);
    badgeLabel = n === 0
      ? t("payments.status.overdue")
      : (n === 1 ? t("payments.urgency.overdue_one", { n }) : t("payments.urgency.overdue_many", { n }));
    badgeClass = "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800/60";
  } else if (isDueSoon) {
    badgeLabel =
      daysUntil === 0 ? t("payments.urgency.due_today")
      : daysUntil === 1 ? t("payments.urgency.due_tomorrow")
      : t("payments.urgency.due_in", { n: daysUntil });
    badgeClass = "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/60";
  }

  const formattedDate = due
    ? due.toLocaleDateString(dateLocale, { day: "numeric", month: "short", year: "numeric" })
    : "—";
  const formattedAmount = `€${Number(p.amount).toLocaleString(dateLocale)}`;

  const secondary = isPaid
    ? (p.paid_at
        ? t("payments.mobile.paid_on", { date: new Date(p.paid_at).toLocaleDateString(dateLocale, { day: "numeric", month: "short", year: "numeric" }) })
        : t("payments.mobile.paid"))
    : isOverdue
      ? t("payments.mobile.overdue_contact")
      : isDueSoon
        ? t("payments.mobile.next_payment")
        : t("payments.mobile.scheduled");

  return (
    <div
      className="dv-card !p-4"
      data-testid={`payment-mobile-${p.payment_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-[15px] leading-tight break-words" data-testid={`payment-mobile-phase-${p.payment_id}`}>
            {paymentLabel(t, p)}
          </div>
          <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">{secondary}</div>
        </div>
        <span
          className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium border whitespace-nowrap ${badgeClass}`}
          data-testid={`payment-mobile-status-${p.payment_id}`}
        >
          {badgeLabel}
        </span>
      </div>

      <div className="flex items-end justify-between gap-3 pt-3 border-t border-stone-100 dark:border-stone-800">
        <div className="min-w-0">
          <div className="text-[10px] tracking-wider uppercase text-stone-400 dark:text-stone-500">{t("payments.col.due_date")}</div>
          <div className="text-sm text-stone-700 dark:text-stone-200 mt-0.5">{formattedDate}</div>
        </div>
        <div className="text-right min-w-0">
          <div className="text-[10px] tracking-wider uppercase text-stone-400 dark:text-stone-500">{t("payments.col.amount")}</div>
          <div className="font-serif-dv text-xl mt-0.5 dv-stat-value" style={{ "--dv-accent": "#2F3E35" }}>
            {formattedAmount}
          </div>
        </div>
      </div>

      {!isPaid && (
        <div className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800">
          {pendingNotif ? (
            <div
              className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 rounded-md px-3 py-2 text-center"
              data-testid={`notif-pending-mobile-${p.payment_id}`}
            >
              {t("payments.notify.pending")}
            </div>
          ) : (
            <button
              onClick={onNotify}
              data-testid={`notify-btn-mobile-${p.payment_id}`}
              className="w-full text-sm text-stone-700 dark:text-stone-200 hover:text-stone-900 dark:hover:text-stone-50 border border-stone-300 dark:border-stone-700 hover:border-stone-900 dark:hover:border-stone-50 rounded-md px-3 py-2 transition-colors"
            >
              {t("payments.notify.btn")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function NotifyPaymentModal({ payment, onClose, onSent, t, dateLocale = "es-ES" }) {
  const [comment, setComment] = useState("");
  const [reference, setReference] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post(`/payments/${payment.payment_id}/notify`, {
        comment: comment || null,
        reference: reference || null,
        receipt: receipt || null,
      });
      await onSent();
    } catch (e) {
      setErr(formatError(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="notify-modal">
      <div className="bg-white dark:bg-stone-900 rounded-lg max-w-md w-full p-6 border border-transparent dark:border-stone-700">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500 dark:text-stone-400 mb-1">{t("payments.notify.title")}</div>
        <div className="font-serif-dv text-2xl mb-1">{paymentLabel(t, payment)}</div>
        <div className="text-stone-500 dark:text-stone-400 text-sm mb-4">
          {t("payments.notify.amount_due", {
            amount: Number(payment.amount).toLocaleString(dateLocale),
            currency: payment.currency,
            date: payment.due_date,
          })}
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-stone-500 dark:text-stone-400">{t("payments.notify.reference_label")}</label>
            <input className="dv-input" value={reference} onChange={e => setReference(e.target.value)} maxLength={100} data-testid="notify-reference" placeholder={t("payments.notify.reference_ph")} />
          </div>
          <div>
            <label className="text-xs text-stone-500 dark:text-stone-400">{t("payments.notify.comment_label")}</label>
            <textarea rows={3} className="dv-input" value={comment} onChange={e => setComment(e.target.value)} maxLength={500} data-testid="notify-comment" placeholder={t("payments.notify.comment_ph")} />
          </div>
          <div>
            <label className="text-xs text-stone-500 dark:text-stone-400 block mb-1.5">{t("payments.notify.receipt_label")}</label>
            <FileUploader
              label={t("payments.notify.receipt_uploader")}
              maxSizeMB={8}
              onSelect={setReceipt}
              onClear={() => setReceipt(null)}
              testId="notify-receipt"
            />
          </div>
          {err && <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/60 rounded px-3 py-2" data-testid="notify-error">{err}</div>}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="dv-btn dv-btn-ghost">{t("payments.notify.cancel")}</button>
            <button type="submit" disabled={busy} className="dv-btn dv-btn-primary flex-1" data-testid="notify-submit">
              {busy ? t("payments.notify.sending") : t("payments.notify.send")}
            </button>
          </div>
        </form>
        <div className="text-xs text-stone-400 dark:text-stone-500 mt-4 text-center">
          {t("payments.notify.hint")}
        </div>
      </div>
    </div>
  );
}
